import { useCallback, useMemo } from "react";
import { Alert, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaArrowCircleRight, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/use-cart";
import { useCartPricing } from "../../hooks/use-cart-pricing";
import { formatCurrency } from "../../lib/format-currency";
import { createCart } from "../../services/cartService";
import { TagList } from "../TagList/TagList";
import "./CartPurchaser.scss";

interface CartPurchaserProps {
  hasFieldsSelected: boolean;
}

const CartPurchaser: React.FC<CartPurchaserProps> = ({ hasFieldsSelected }) => {
  const { cart, isCartEmpty, clearCartWithConfirmation } = useCart();
  const cartPricing = useCartPricing(cart);
  const navigate = useNavigate();

  const summaryTags = useMemo(() => {
    const tags = [];
    if (cart?.items.length) {
      tags.push(`${cart?.items.length} items`);
    } else {
      tags.push("Empty Cart");
    }

    if (hasFieldsSelected) {
      tags.push("Fields Selected");
    } else {
      tags.push("No Fields Selected");
    }
    return tags;
  }, [cart, hasFieldsSelected]);

  const handleSaveCart = useCallback(async () => {
    if (!cart) {
      toast.error("No cart to save");
      return;
    }

    await createCart(cart, cartPricing);
    toast.success("Cart saved");
    navigate(`/cart/${cart.id}`);
  }, [cart, cartPricing, navigate]);

  return (
    <div className="cart-purchaser">
      <div className="two-column-grid">
        <div className="column">
          <div className="cart-purchaser-header">Summary</div>
          <div className="cart-purchaser-summary">
            <TagList tags={summaryTags} />
          </div>
          <div className="cart-purchaser-actions">
            <Button
              className="icon-button"
              variant="primary"
              onClick={clearCartWithConfirmation}
              disabled={isCartEmpty}
            >
              <FaTrash />
              Empty Cart
            </Button>
          </div>
        </div>
        <div className="column">
          <div className="cart-purchaser-header">Purchase</div>
          {isCartEmpty ? (
            <Alert variant="info">
              Your cart is empty. Add some contacts to get started.
            </Alert>
          ) : (
            <div className="cart-purchaser-purchase-details">
              <div className="cart-purchaser-pricing-row">
                <span>Pricing Tier</span>
                <span>{cartPricing.tierDescription}</span>
              </div>
              <div className="cart-purchaser-pricing-row">
                <span>Total</span>
                <span>{formatCurrency(cartPricing.totalX100)}</span>
              </div>
              <div className="actions">
                <div>
                  <Button
                    className="icon-button"
                    variant="primary"
                    onClick={handleSaveCart}
                  >
                    Save Cart &amp; Purchase{" "}
                    <FaArrowCircleRight className="icon-right" />
                  </Button>
                  <div className="caption">
                    You can complete your purchase with a credit card or digital
                    wallet.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPurchaser;
